import React, { Fragment, useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import {
  Column,
  Table as ReactTable,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  ColumnDef,
} from '@tanstack/react-table';
import { rankItem } from '@tanstack/match-sorter-utils';
// import { useSelector } from 'react-redux';
// import { RootState } from 'slices';
import {
  EntriesPagination,
  ResultsPagination,
} from 'Components/pos2u/Pagination';
import { useTranslation } from 'react-i18next';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { ProductOptionDetail } from 'types/product/productOptionTypes';
import { putUpdateSortOrderProductOption } from 'services/ProductOptionService';
import { toast } from 'react-toastify';
import useWindowSize from 'Components/Hooks/WindowSizeHook';

const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();
  return (
    <input
      type="text"
      value={typeof columnFilterValue === 'string' ? columnFilterValue : ''}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder="Search..."
      className="form-control search"
    />
  );
};

// const SortableRow = ({
//   row,
//   children,
// }: {
//   row: any;
//   children: React.ReactNode;
// }) => {
//   const { attributes, listeners, setNodeRef, transform, transition } =
//     useSortable({
//       id: row.original.id,
//     });

//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     cursor: 'move',
//   };

//   return (
//     <tr
//       ref={setNodeRef}
//       style={style}
//       {...attributes}
//       {...listeners}
//       className={`row-${row.original.id}`}
//     >
//       {children}
//     </tr>
//   );
// };

const SortableRow = ({
  row,
  children,
}: {
  row: any;
  children: React.ReactNode;
}) => {
  const { setNodeRef, transform, transition } = useSortable({
    id: row.original.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // cursor: 'grab',
  };

  return (
    <tr ref={setNodeRef} style={style} className={`row-${row.original.id}`}>
      {children}
    </tr>
  );
};

interface TableContainerProps {
  columns: ColumnDef<any, any>[];
  data?: any;
  customPageSize: number;
  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;
  TotalData: number;
  totalPages: number;
  currentPage: number;
  setEntriesPerPage: (value: number) => void;
  handlePageChange: (value: number) => void;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
}

const ProductOptionsTable = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  TotalData,
  totalPages,
  currentPage,
  setEntriesPerPage,
  handlePageChange,
  inputPageValue,
  setInputPageValue,
}: TableContainerProps) => {
  // const layoutState = useSelector((state: RootState) => state.Layout);
  // const { layoutModeType } = layoutState;
  // const isDarkMode = layoutModeType === 'dark';
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const [globalFilter, setGlobalFilter] = useState('');
  const [localData, setLocalData] = useState<ProductOptionDetail[]>(data);
  useEffect(() => {
    if (data && Array.isArray(data)) {
      setLocalData(data);
    }
  }, [data]);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data: localData,
    filterFns: { fuzzy: fuzzyFilter },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  useEffect(() => {
    if (Number(customPageSize)) setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  const handlePreviousPage = () => {
    if (currentPage > 0) handlePageChange(currentPage - 1);
    setInputPageValue(currentPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      handlePageChange(currentPage + 1);
      setInputPageValue(currentPage + 2);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor));
  //? ===== OLD  ====== //
  // const onDragEnd = (event: DragEndEvent) => {
  //   const { active, over } = event;
  //   if (!over || active.id === over.id) return;

  //   const oldIndex = localData.findIndex((item: any) => item.id === active.id);
  //   const newIndex = localData.findIndex((item: any) => item.id === over.id);

  //   const newData = arrayMove(localData, oldIndex, newIndex).map(
  //     (item, index) => ({
  //       ...item,
  //       sort_order: index + 1,
  //     })
  //   );

  //   setLocalData(newData);

  //   // Call API to update sort_order on server
  //   fetch('/api/product-addons/sort', {
  //     method: 'POST',
  //     headers: { 'Content-Type': 'application/json' },
  //     body: JSON.stringify({ updatedOrder: newData }),
  //   })
  //     .then((res) => res.ok && console.log('Sort order updated'))
  //     .catch(() => console.error('Failed to update sort order'));
  // };

  const onDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localData.findIndex((item: any) => item.id === active.id);
    const newIndex = localData.findIndex((item: any) => item.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reorder list
    const newData = arrayMove(localData, oldIndex, newIndex);

    // Update sort_order
    // const updatedData = newData.map((item, index) => ({
    //   ...item,
    //   sort_order: newData.length - index,
    // }));
    const updatedData = newData.map((item, index) => ({
      ...item,
      // sort_order: newData.length - index,
      sort_order: index + 1,
    }));

    setLocalData(updatedData);

    // Get only the moved item
    // const movedItem: any = updatedData[newIndex];
    const requestBody = newData.map((item, index) => ({
      id: item.id,
      // sort_order: newData.length - index,
      sort_order: index + 1,
    }));

    try {
      const response = await putUpdateSortOrderProductOption(requestBody);
      if (response) {
        const { message, error_code, data } = response;
        if (message === 'OK' && error_code === 0) {
          toast.success(data.message, { autoClose: 3000 });
        } else {
          toast.error(message, { autoClose: 3000 });
        }
      } else {
        toast.error('No response data returned', { autoClose: 3000 });
        console.error('No response data returned');
      }
    } catch (error) {
      console.log(error);
      toast.error('Unexpected error occurred. Please try again.', {
        autoClose: 3000,
      });
    }
  };

  return (
    <Fragment>
      {isMobile && (
        <EntriesPagination
          customPageSize={customPageSize}
          setEntriesPerPage={setEntriesPerPage}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
          itemsPerPage={[10]}
        />
      )}

      <div className={divClass}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={localData.map((item: any) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <Table hover className={tableClass} style={{ width: '100%' }}>
              <thead className={theadClass}>
                {getHeaderGroups().map((headerGroup) => (
                  <tr className={trClass} key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={thClass}
                        // style={{ ...header.column.columnDef.meta?.style }}
                        style={{
                          ...(
                            header.column.columnDef.meta as {
                              style?: React.CSSProperties;
                            }
                          )?.style,
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {header.column.getCanFilter() && (
                              <Filter column={header.column} table={table} />
                            )}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {getRowModel().rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={getHeaderGroups()[0].headers.length}
                      className="text-center"
                    >
                      {t('Not found for product option')}
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => (
                    <SortableRow key={row.original.id} row={row}>
                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={`${row.original.id}_${index}`}
                          className={`cell-${row.original.id}-${cell.row.id}`}
                          // style={{ ...cell.column.columnDef.meta?.style }}
                          style={{
                            ...(
                              cell.column.columnDef.meta as {
                                style?: React.CSSProperties;
                              }
                            )?.style,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </SortableRow>
                  ))
                )}
              </tbody>
            </Table>
          </SortableContext>
        </DndContext>
      </div>

      <ResultsPagination
        showingData={getState().pagination.pageSize}
        TotalData={TotalData}
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        handlePreviousPage={handlePreviousPage}
        handleNextPage={handleNextPage}
        inputPageValue={inputPageValue}
        setInputPageValue={setInputPageValue}
      />
    </Fragment>
  );
};

export default ProductOptionsTable;
